import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
//
import {
  Box,
  Checkbox,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Iconify } from '@/components';
import { VBMActionButtons, VBMCardTitle, VBMEditButton } from '..';
import { PETS, transformPets } from '@/features/bookings-management/utils';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useResponsive from '@/hooks/useResponsive';
import { updateBookingSession } from '@/features/bookings-management/api';
import { BookingSession } from '@/features/bookings-management/types';
import { useSelector } from '@/stores';

const PET_LABELS = PETS.reduce<Record<string, string>>((acc, pet) => {
  acc[pet.value] = pet.name;
  return acc;
}, {});

const normalizePets = (pets: unknown) => {
  if (Array.isArray(pets)) {
    return Array.from(
      new Set(
        pets
          .map((pet) => String(pet).trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }
  if (typeof pets === 'string') {
    return Array.from(
      new Set(
        transformPets(pets)
          .map((pet) => pet.trim().toLowerCase())
          .filter(Boolean)
      )
    );
  }
  return [];
};

type VBMPetsProps = {
  editable?: boolean;
  bookingSession?: BookingSession;
  sessionView?: boolean;
};

export default function VBMPets({
  editable = false,
  bookingSession,
  sessionView = false,
}: VBMPetsProps) {
  const downMd = useResponsive('down', 'md');
  const booking = useSelector((state) => state.booking);
  const { successMsg, errorMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  const rawPets =
    sessionView && bookingSession
      ? bookingSession.pets
      : booking.cleanDetails.pets_enabled
        ? booking.cleanDetails.pets
        : [];
  const resolvedPets = useMemo(() => normalizePets(rawPets), [rawPets]);

  const [isEdit, setIsEdit] = useState(false);
  const [selectedPets, setSelectedPets] = useState<string[]>(resolvedPets);
  const [applyToAllFutureSessions, setApplyToAllFutureSessions] =
    useState(false);

  useEffect(() => {
    if (!isEdit) {
      setSelectedPets(resolvedPets);
    }
  }, [isEdit, resolvedPets]);

  const bookingId = bookingSession?.booking_id ?? null;
  const canApplyToAllSessions = Boolean(bookingId);

  const updatePetsMutation = useMutation({
    mutationFn: async ({
      petsValue,
      applyToAllFuture,
    }: {
      petsValue: string;
      applyToAllFuture: boolean;
    }) => {
      if (!bookingSession?.id) {
        throw new Error('Missing booking session id');
      }

      await updateBookingSession(bookingSession.id, {
        pets: petsValue,
        apply_to_all_future: applyToAllFuture,
      });
    },
    onSuccess: (_data, variables) => {
      successMsg(
        variables.applyToAllFuture
          ? 'Pets updated for this and future sessions'
          : 'Pets updated for this session'
      );
      setIsEdit(false);
      setApplyToAllFutureSessions(false);
      queryClient.invalidateQueries({ queryKey: ['bookingSessions'], exact: false });
      if (bookingSession?.id) {
        queryClient.invalidateQueries({
          queryKey: ['bookingSession', String(bookingSession.id)],
          exact: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['bookings'], exact: false });
    },
    onError: (error) => {
      errorMsg(error, 'Failed to update pets');
    },
  });

  const handleStartEdit = () => {
    setApplyToAllFutureSessions(false);
    setSelectedPets(resolvedPets);
    setIsEdit(true);
  };

  const handleCancelEdit = () => {
    setApplyToAllFutureSessions(false);
    setSelectedPets(resolvedPets);
    setIsEdit(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updatePetsMutation.mutate({
      petsValue: selectedPets.join('|'),
      applyToAllFuture: applyToAllFutureSessions,
    });
  };

  const petsDisplay = resolvedPets
    .map((pet) => PET_LABELS[pet] ?? pet)
    .join(' + ');

  const isSaving = updatePetsMutation.isLoading;
  const canEdit = editable && sessionView && !!bookingSession;

  return (
    <Stack spacing={1} sx={{ position: 'relative' }}>
      {!isEdit && canEdit && <VBMEditButton onClickButton={handleStartEdit} />}
      <VBMCardTitle title="Pets" sx={{ mb: 1 }} />

      {isEdit ? (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <ToggleButtonGroup
              orientation={downMd ? 'vertical' : 'horizontal'}
              color="primary"
              value={selectedPets}
              exclusive={false}
              onChange={(_, value) => setSelectedPets(value ?? [])}
              aria-label="Pet selector"
              sx={{
                maxWidth: 'fit-content',
                height: downMd ? 'auto' : 46,
              }}
            >
              {PETS.map((pet) => (
                <ToggleButton key={pet.id} value={pet.value} sx={{ width: 120 }}>
                  {pet.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox
                size="small"
                checked={applyToAllFutureSessions}
                onChange={(event) =>
                  setApplyToAllFutureSessions(event.target.checked)
                }
                disabled={!canApplyToAllSessions || isSaving}
              />
              <Stack spacing={0.25}>
                <Typography
                  variant="body2"
                  color={!canApplyToAllSessions ? 'text.disabled' : 'text.primary'}
                >
                  Apply to all future sessions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {canApplyToAllSessions
                    ? 'Updates upcoming sessions in this booking.'
                    : 'Available once a booking has been created.'}
                </Typography>
              </Stack>
            </Stack>

            <VBMActionButtons
              onClickCancel={handleCancelEdit}
              isSubmitting={isSaving}
              disabled={isSaving}
            />
          </Stack>
        </form>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 35 }}>
            <Iconify icon="ph:dog-fill" width={downMd ? 20 : 30} />
          </Box>
          <Typography fontSize={downMd ? 12 : 16}>
            {petsDisplay || 'No pets'}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
